import React from 'react';
import { Typography } from '@mui/material';
import LegalPage from '../../components/LegalPage/LegalPage';

const Privacy = () => (
    <LegalPage product="Relayr" title="Privacy Policy">
        <p><strong>Effective Date:</strong> April 14, 2026</p>
        <p>
            Relayr ("we", "our", or "us") operates the Relayr mobile application. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our app. Please read this policy carefully. By using Relayr, you agree to the collection and use of information in accordance with this policy.
        </p>

        <Typography variant="h6">1. Information We Collect</Typography>
        <p>We may collect the following types of information:</p>
        <ul>
            <li><strong>Account Information:</strong> Email address, username, display name, profile photo, and password</li>
            <li><strong>User-Generated Content:</strong> Messages, replies, mentions, and any media shared within channels and networks</li>
            <li><strong>Device Information:</strong> Device type, operating system, app version, and unique device identifiers</li>
            <li><strong>Log Data:</strong> IP address, timestamps, access times, pages viewed, and actions taken within the app</li>
            <li><strong>Analytics &amp; Crash Data:</strong> We may use third-party tools such as Sentry for error monitoring and crash reporting to improve app stability</li>
        </ul>

        <Typography variant="h6">2. How We Use Your Information</Typography>
        <p>We use the information we collect to:</p>
        <ul>
            <li>Provide, operate, and maintain the Relayr service</li>
            <li>Enable real-time communication between users across networks and channels</li>
            <li>Improve, personalize, and expand app features and functionality</li>
            <li>Moderate content and enforce community guidelines</li>
            <li>Send push notifications related to messages, mentions, and activity</li>
            <li>Monitor and analyze usage trends and performance</li>
            <li>Detect, prevent, and address technical issues or security threats</li>
        </ul>

        <Typography variant="h6">3. Data Sharing</Typography>
        <p>We do not sell your personal data to third parties.</p>
        <p>We may share your information in the following limited circumstances:</p>
        <ul>
            <li><strong>Service Providers:</strong> We may share data with trusted third-party providers who assist us in operating the app, including cloud hosting, analytics, and error monitoring services. These providers are bound by confidentiality obligations.</li>
            <li><strong>Legal Compliance:</strong> We may disclose your information if required to do so by law, or in response to valid requests by public authorities (e.g., a court order or government agency).</li>
            <li><strong>Safety &amp; Enforcement:</strong> We may share information when we believe disclosure is necessary to protect the rights, property, or safety of Relayr, our users, or the public.</li>
        </ul>

        <Typography variant="h6">4. Data Retention</Typography>
        <p>
            We retain your personal information for as long as your account is active or as needed to provide you with our services. If you delete your account, we will remove your personal data within a reasonable timeframe, except where retention is required by law or for legitimate business purposes such as resolving disputes or enforcing agreements.
        </p>
        <p>
            Messages and content shared in public channels may remain visible to other users even after account deletion.
        </p>

        <Typography variant="h6">5. Security</Typography>
        <p>
            We implement industry-standard security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure server infrastructure, and access controls.
        </p>
        <p>
            However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your data, we cannot guarantee its absolute security.
        </p>

        <Typography variant="h6">6. Your Rights</Typography>
        <p>You have the right to:</p>
        <ul>
            <li>Access the personal data we hold about you</li>
            <li>Update or correct inaccurate information</li>
            <li>Request deletion of your account and associated data</li>
            <li>Opt out of non-essential push notifications</li>
        </ul>
        <p>
            To exercise any of these rights, please contact us at <a href="mailto:support@relayr.app">support@relayr.app</a>.
        </p>

        <Typography variant="h6">7. Children's Privacy</Typography>
        <p>
            Relayr is not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that a child under 13 has provided us with personal data, we will take steps to delete such information promptly. If you believe a child has provided us with their data, please contact us immediately.
        </p>

        <Typography variant="h6">8. Changes to This Policy</Typography>
        <p>
            We may update this Privacy Policy from time to time. When we make changes, we will update the "Effective Date" at the top of this page and notify users through the app or via email. Your continued use of Relayr after any changes constitutes acceptance of the updated policy.
        </p>

        <Typography variant="h6">9. Contact Us</Typography>
        <p>
            If you have any questions or concerns about this Privacy Policy, please contact us at:
        </p>
        <p>Email: <a href="mailto:support@relayr.app">support@relayr.app</a></p>
    </LegalPage>
);

export default Privacy;
